import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  descadastrarPorConvite,
  faltamResponder,
  lerPesquisaPorConvite,
  responderPorConvite,
  voltarAReceberPorConvite,
  type EstadoDoConvite,
  type PesquisaDoAluno,
} from "../lib/pesquisas";
import { rolarAte } from "../lib/rolarPara";
import { diaDoFim } from "../../../supabase/functions/_shared/convite-de-pesquisa";
import Carregando from "../componentes/Carregando";
import FormularioDeNota from "../componentes/FormularioDeNota";
import { LogoCF, Recado } from "../componentes/ui";

// A pesquisa que chega por E-MAIL (000320 — o G1 do Plano de Melhorias da Equipe
// de Produtos, com a decisão de Victor de 28/09: "link pessoal, sem login").
//
// ---------------------------------------------------------------------------
// Quem abre isto, e por que não há login
// ---------------------------------------------------------------------------
// O aluno, pelo link do convite. Ele NÃO tem conta no hub — 4 perfis de 67 mil
// têm uma (medido em 28/09), todos da equipe —, e mandá-lo para uma tela de
// login seria abrir o hub a ele pelo primeiro acesso, com os cursos trancados
// pelo contrato. Quem faz o papel da autenticação é o TOKEN do convite, como no
// orçamento do IR Proposta. Por isso a página não tem o Topo nem o Rodapé do
// hub: menu de aluno e link para a Faculdade convidariam ao clique que leva a
// uma tela que ele não vai passar.
//
// ---------------------------------------------------------------------------
// O número do e-mail só MARCA
// ---------------------------------------------------------------------------
// Na pesquisa de curso o aluno escolhe a nota no próprio e-mail, e o link abre
// com `?nota=N`. A página marca a nota e espera ele confirmar: leitor de links
// corporativo segue todo link de todo e-mail, e gravar na chegada transformaria
// a varredura dele em voto. A frase diz isso com letra, para ele não sair
// achando que já respondeu.
//
// O descadastro segue a mesma regra: `?descadastrar=1` (o link do rodapé do
// e-mail) abre a confirmação, e só o clique descadastra.
export default function PesquisaPorConvite() {
  const { token = "" } = useParams();
  const [busca] = useSearchParams();
  const [estado, setEstado] = useState<EstadoDoConvite | null | "carregando">("carregando");

  const recarregar = useCallback(async () => {
    const e = await lerPesquisaPorConvite(token);
    setEstado(e);
    return e;
  }, [token]);

  useEffect(() => {
    let vivo = true;
    void lerPesquisaPorConvite(token).then((e) => {
      if (vivo) setEstado(e);
    });
    return () => {
      vivo = false;
    };
  }, [token]);

  const bruta = busca.get("nota");
  const notaDoEmail = bruta !== null && /^(10|[0-9])$/.test(bruta) ? Number(bruta) : null;
  const pediuDescadastro = busca.get("descadastrar") === "1";

  if (estado === "carregando") return <Carregando texto="Abrindo a pesquisa…" />;

  if (estado === null) {
    return (
      <Moldura>
        <Situacao titulo="Não conseguimos abrir a pesquisa agora" chave="sem-conexao">
          Confira a conexão e recarregue a página. O link continua valendo.
        </Situacao>
      </Moldura>
    );
  }

  if (!estado.ok) {
    const titulo =
      estado.motivo === "fechada"
        ? "Esta pesquisa foi encerrada"
        : estado.motivo === "sem_curso"
          ? "Esta pesquisa não está mais disponível para você"
          : "Link de pesquisa inválido";
    return (
      <Moldura>
        <Situacao titulo={titulo} chave={estado.motivo} curso={estado.curso_nome}>
          {estado.detalhe.charAt(0).toUpperCase() + estado.detalhe.slice(1)}
        </Situacao>
        {/* Com a pesquisa encerrada ou o curso vencido, o link continua servindo
            para sair da lista das PRÓXIMAS — que é o motivo mais comum de alguém
            abrir um convite velho. Com o link inválido, não há de quem. */}
        {estado.motivo !== "link_invalido" && (
          <Descadastro token={token} descadastrado={false} abrir={pediuDescadastro} aoMudar={recarregar} />
        )}
      </Moldura>
    );
  }

  return (
    <Moldura>
      <Pesquisa token={token} pesquisa={estado.pesquisa} notaDoEmail={notaDoEmail} aoResponder={recarregar} />
      <Descadastro token={token} descadastrado={estado.descadastrado} abrir={pediuDescadastro} aoMudar={recarregar} />
    </Moldura>
  );
}

function Pesquisa({
  token,
  pesquisa,
  notaDoEmail,
  aoResponder,
}: {
  token: string;
  pesquisa: PesquisaDoAluno;
  notaDoEmail: number | null;
  aoResponder: () => Promise<unknown>;
}) {
  const idTitulo = useId();
  const doCurso = pesquisa.alvo === "curso";
  const faltam = faltamResponder(pesquisa);
  // A nota do e-mail só aparece como aviso enquanto ela é DIFERENTE do que está
  // gravado: se o aluno já confirmou esse mesmo número, não há o que confirmar.
  const notaPendente = doCurso && notaDoEmail !== null && notaDoEmail !== (pesquisa.resposta?.nota ?? null) ? notaDoEmail : null;

  return (
    <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby={idTitulo} data-pesquisa={pesquisa.id}>
      <p className="text-xs font-bold tracking-widest text-cinza uppercase">{pesquisa.curso_nome}</p>
      <h1 id={idTitulo} className="mt-1 font-display text-2xl font-bold text-titulo">
        {pesquisa.titulo}
      </h1>
      <p className="mt-2 text-tinta">{pesquisa.pergunta}</p>
      {pesquisa.fecha_em && (
        <p className="mt-1 text-sm text-cinza">Aberta até {diaDoFim(pesquisa.fecha_em)}.</p>
      )}
      {/* A promessa vem ANTES do formulário: é ela que decide o que a pessoa
          escreve (o desenho da página Avaliações). */}
      <p className="mt-3 text-sm text-cinza" data-promessa>
        A equipe vê a sua nota e o seu comentário — <strong className="font-semibold text-tinta">não o seu nome</strong>.
        {!doCurso && " Cada professor também vê a nota e o comentário que recebeu, sem saber quem escreveu."} Você pode
        mudar a resposta por este mesmo link enquanto a pesquisa estiver aberta.
      </p>

      {notaPendente !== null && (
        <p className="mt-4 rounded-xl bg-alerta-fundo p-3.5 text-sm text-alerta-tinta" data-nota-do-email={notaPendente}>
          Você escolheu <strong>{notaPendente}</strong> no e-mail. Confira e toque em «
          {pesquisa.resposta ? "Atualizar resposta" : "Enviar resposta"}» — nada foi gravado ainda.
        </p>
      )}

      {doCurso ? (
        <FormularioDeNota
          chave="convite-curso"
          rotulo={pesquisa.titulo}
          atual={pesquisa.resposta}
          notaInicial={notaDoEmail}
          enviar={(nota, comentario) => responderPorConvite(token, null, nota, comentario)}
          aoResponder={aoResponder}
        />
      ) : (pesquisa.docentes ?? []).length === 0 ? (
        <p className="mt-4 text-sm text-cinza" data-sem-professores>
          Esta pesquisa é sobre os professores da sua turma, e não achamos uma turma com professores na sua matrícula
          deste curso. Se isso parece errado, responda o e-mail do convite.
        </p>
      ) : (
        <>
          <p className="mt-4 text-sm text-tinta" data-faltam={faltam}>
            {faltam === 0 ? "Você já avaliou todos. Obrigado!" : faltam === 1 ? "Falta 1 professor." : `Faltam ${faltam} professores.`}
          </p>
          <ul className="mt-2 divide-y divide-borda">
            {(pesquisa.docentes ?? []).map((d) => (
              <li key={d.id} className="py-4" data-docente={d.nome}>
                <p className="font-semibold text-tinta">{d.nome}</p>
                <FormularioDeNota
                  chave={`convite-${d.id}`}
                  rotulo={d.nome}
                  atual={d.resposta}
                  enviar={(nota, comentario) => responderPorConvite(token, d.id, nota, comentario)}
                  aoResponder={aoResponder}
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/**
 * Sair (ou voltar para) a lista das pesquisas por e-mail. O clique é o que
 * descadastra — chegar pelo link do e-mail só abre a confirmação.
 */
function Descadastro({
  token,
  descadastrado,
  abrir,
  aoMudar,
}: {
  token: string;
  descadastrado: boolean;
  abrir: boolean;
  aoMudar: () => Promise<unknown>;
}) {
  const [confirmando, setConfirmando] = useState(abrir && !descadastrado);
  const [enviando, setEnviando] = useState(false);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  const caixa = useRef<HTMLElement>(null);

  // Quem chegou pelo "descadastre-se" do e-mail veio para isto: a confirmação
  // entra no campo de visão (sem roubar o foco de ninguém — `rolarAte`).
  useEffect(() => {
    if (abrir) rolarAte(caixa.current);
  }, [abrir]);

  async function agir(fazer: () => Promise<{ ok: boolean; detalhe?: string }>) {
    setEnviando(true);
    setRecado(null);
    const r = await fazer();
    setEnviando(false);
    setConfirmando(false);
    const texto = r.detalhe ?? (r.ok ? "Pronto." : "Não foi possível agora.");
    setRecado({ tipo: r.ok ? "ok" : "erro", texto: texto.charAt(0).toUpperCase() + texto.slice(1) });
    if (r.ok) await aoMudar();
  }

  return (
    <section ref={caixa} className="mt-6 rounded-2xl border border-borda p-5 text-sm" data-descadastro={descadastrado ? "fora" : "dentro"}>
      {descadastrado ? (
        <>
          <p className="text-tinta">Você não recebe pesquisas por e-mail.</p>
          <button
            type="button"
            className="btn-contorno mt-3 min-h-10 text-sm"
            disabled={enviando}
            onClick={() => void agir(() => voltarAReceberPorConvite(token))}
            data-voltar-a-receber
          >
            {enviando ? "Registrando…" : "Voltar a receber"}
          </button>
        </>
      ) : !confirmando ? (
        <p className="text-cinza">
          Não quer receber pesquisas por e-mail?{" "}
          <button type="button" className="font-semibold text-link underline" onClick={() => setConfirmando(true)} data-descadastrar>
            Descadastre-se
          </button>
          .
        </p>
      ) : (
        <div data-confirmar-descadastro>
          <p className="text-tinta">
            Você deixa de receber as <strong className="font-semibold">próximas</strong> pesquisas por e-mail. Esta
            continua aberta aqui, se quiser responder.
          </p>
          <div className="mt-3 flex flex-wrap gap-3">
            <button
              type="button"
              className="pill-cta"
              disabled={enviando}
              onClick={() => void agir(() => descadastrarPorConvite(token))}
              data-confirmar
            >
              {enviando ? "Registrando…" : "Não quero receber"}
            </button>
            <button type="button" className="btn-contorno min-h-10 text-sm" onClick={() => setConfirmando(false)}>
              Cancelar
            </button>
          </div>
        </div>
      )}
      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
    </section>
  );
}

function Situacao({
  titulo,
  chave,
  curso,
  children,
}: {
  titulo: string;
  chave: string;
  curso?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="surface p-7 text-center" data-situacao={chave}>
      {curso && <p className="text-xs font-bold tracking-widest text-cinza uppercase">{curso}</p>}
      <h1 className="mt-1 font-display text-xl font-bold text-titulo">{titulo}</h1>
      <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-cinza">{children}</p>
    </div>
  );
}

/**
 * A moldura da página pública — a do orçamento do IR Proposta: a marca no alto,
 * e nada do hub do aluno.
 */
function Moldura({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col bg-fundo">
      <header className="bg-azul">
        <div className="mx-auto flex max-w-3xl items-center px-4 py-4 sm:px-6">
          <LogoCF className="h-7" />
        </div>
      </header>
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-8 sm:px-6">{children}</main>
      <footer className="bg-azul py-5 text-center">
        <p className="text-xs text-navy-mute">Pesquisa de satisfação do Contabilidade Facilitada</p>
      </footer>
    </div>
  );
}
